import { supabase } from "@/integrations/supabase/client";
import { dbRowToItem } from "@/hooks/use-public-places";
import { hasDistrictHubTag } from "@/lib/item-link";
import { hasTfPendingTag } from "@/lib/tf-pending";
import type { Item } from "@/data/places";
import type { PublicCity } from "@/hooks/use-public-cities";

// Card columns only — mirrors the client `public-places` query shape.
const PLACE_CARD_COLUMNS =
  "id,slug,section,city_slug,name,category,category_slug,area,address,description,image_url,rating,tags,link,reserve_link,cta,date_label,starts_at,ends_at,price,lat,lng,show_countdown,event_status_override,buy_ticket_url,bokun_product_id,region,listing_type,menu_external_url,menu_pdf_url,menu_image_url";

/** Keep the SSR payload small: only what can plausibly be above the fold. */
const SEED_PLACE_LIMIT = 24;

export type HomeSeed = {
  cities: PublicCity[];
  places: Item[];
};

export async function fetchHomeSeed(): Promise<HomeSeed> {
  const [citiesRes, placesRes] = await Promise.all([
    supabase
      .from("cities")
      .select("slug, name, region, tagline, image_url, accent, population, founded, lat, lng, sort_order")
      .eq("active", true)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true }),
    supabase
      .from("places")
      .select(PLACE_CARD_COLUMNS)
      .eq("published", true)
      .order("sort_order", { ascending: true })
      .limit(SEED_PLACE_LIMIT),
  ]);

  const cities: PublicCity[] = (citiesRes.data ?? []).map((c: any) => ({
    slug: c.slug,
    name: c.name,
    region: c.region,
    tagline: c.tagline,
    image_url: c.image_url,
    accent: c.accent,
    population: c.population,
    founded: c.founded,
    lat: c.lat,
    lng: c.lng,
  }));

  const places: Item[] = (placesRes.data ?? [])
    .filter((r: any) => !hasTfPendingTag(r.tags) && !hasDistrictHubTag(r.tags))
    .map((r: any) => dbRowToItem(r));

  return { cities, places };
}
